import { useState, useEffect, useCallback } from 'react'
import { useInvoiceAnalyser } from '@/hooks/useInvoiceAnalyser'
import { useDatabase } from '@/hooks/useDatabase'
import { sendAnalyserResultToFlixpector } from '@/hooks/useInbox'
import { DocumentDropzone } from '@/components/InvoiceAnalyser/Dropzone'
import { ProcessingStatus } from '@/components/InvoiceAnalyser/ProcessingStatus'
import { ExtractionResult } from '@/components/InvoiceAnalyser/ExtractionResult'
import { HistoryPanel } from '@/components/InvoiceAnalyser/HistoryPanel'
import { EvidenceDialog } from '@/components/InvoiceAnalyser/EvidenceDialog'
import { DuplicateWarning } from '@/components/InvoiceAnalyser/DuplicateWarning'
import type { Evidence, ExtractionHistory, InvoiceExtraction } from '@/types/invoiceAnalyser'
import { loadSettings } from '@/utils/storage'
import { Banner, Button, Icon, Notice, Page, PageHeader } from '@/components/ui'
import { useT } from '@/i18n/useT'

export default function InvoiceAnalyserPage() {
  const { t } = useT()
  const [history, setHistory] = useState<ExtractionHistory[]>([])
  const [historyOpen, setHistoryOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [currentResult, setCurrentResult] = useState<InvoiceExtraction | null>(null)
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null)
  const [currentFilename, setCurrentFilename] = useState<string | null>(null)
  const [evidenceField, setEvidenceField] = useState<keyof Evidence | null>(null)
  const [evidenceOpen, setEvidenceOpen] = useState(false)
  const [showDropzone, setShowDropzone] = useState(true)
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  // Severity is its own flag rather than a prefix sniffed off the message: the
  // old `startsWith('Error')` test would silently stop matching once the text
  // is translated, and every failure would render as a green success notice.
  const [toastIsError, setToastIsError] = useState(false)
  const [sendingToFlixpector, setSendingToFlixpector] = useState(false)
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [pendingExtraction, setPendingExtraction] = useState<InvoiceExtraction | null>(null)
  const [duplicateInfo, setDuplicateInfo] = useState<{
    supplierName: string
    rechnungsnummer: string
    dateInvoice: string | null
    duplicateId: string | null
  } | null>(null)

  const {
    isUploading,
    currentJob,
    getHistory,
    uploadAndProcess,
    deleteDocument,
    stopPolling,
    resetCurrentJob,
    addSupplierToWhitelist,
    addRecipientToWhitelist,
  } = useInvoiceAnalyser()

  const { createInvoice, checkDuplicate } = useDatabase()

  /** Every toast goes through here, so severity and lifetime stay in one place. */
  const showToast = useCallback((message: string, isError = false) => {
    setToastMessage(message)
    setToastIsError(isError)
    setTimeout(() => setToastMessage(null), isError ? 6000 : 3000)
  }, [])

  // Refresh history
  const refreshHistory = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getHistory()
      setHistory(data)
    } catch (error) {
      console.error('Failed to load history:', error)
    } finally {
      setLoading(false)
    }
  }, [getHistory])

  // Load history on mount
  useEffect(() => {
    void refreshHistory()
  }, [refreshHistory])

  // Handle file selection and processing
  const handleFileSelect = useCallback(async (file: File) => {
    setShowDropzone(false)
    setCurrentResult(null)
    setCurrentFilename(file.name)

    const settings = loadSettings()
    const ocrModel = settings.analyserOcrModels?.[0]
    const extractionModel = settings.analyserExtractionModels?.[0]

    try {
      // Upload and process in one go
      const { result, documentId } = await uploadAndProcess(
        file,
        {
          ocrModel,
          extractionModel,
        },
        () => {
          // Job update callback
        }
      )

      setCurrentResult(result)
      setCurrentDocumentId(documentId)
      setShowDropzone(true)
      await refreshHistory()
    } catch (error) {
      console.error('Processing failed:', error)
      setShowDropzone(true)
    }
  }, [uploadAndProcess, refreshHistory])

  // Handle viewing evidence — optionally focused on the field that was clicked
  const handleViewEvidence = useCallback((field?: keyof Evidence) => {
    setEvidenceField(field ?? null)
    setEvidenceOpen(true)
  }, [])

  // Handle history item click
  const handleHistoryItemClick = useCallback((item: ExtractionHistory) => {
    if (item.result) {
      setCurrentResult(item.result)
      setCurrentDocumentId(item.documentId)
      setCurrentFilename(item.filename)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  // Reset for new upload
  const handleNewUpload = useCallback(() => {
    stopPolling()
    resetCurrentJob()
    setCurrentResult(null)
    setCurrentDocumentId(null)
    setCurrentFilename(null)
    setShowDropzone(true)
  }, [resetCurrentJob, stopPolling])

  // Handle delete history item
  const handleDeleteItem = useCallback(async (documentId: string) => {
    try {
      await deleteDocument(documentId)
      await refreshHistory()
      // Clear current result if it was the deleted item
      if (currentDocumentId === documentId) {
        setCurrentResult(null)
        setCurrentDocumentId(null)
        setCurrentFilename(null)
        setShowDropzone(true)
      }
    } catch (error) {
      console.error('Failed to delete document:', error)
    }
  }, [currentDocumentId, deleteDocument, refreshHistory])

  // Handle adding supplier to whitelist
  const handleAddSupplier = useCallback(async (supplierName: string) => {
    const id = supplierName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    try {
      await addSupplierToWhitelist({
        id,
        names: [supplierName],
        normalizedName: supplierName,
        ustId: ''
      })
      showToast(t('"{name}" added to the supplier whitelist').replace('{name}', supplierName))
    } catch (error) {
      console.error('Failed to add supplier:', error)
      showToast(error instanceof Error ? error.message : t('Could not add the supplier'), true)
    }
  }, [addSupplierToWhitelist, showToast, t])

  // Handle adding recipient to whitelist
  const handleAddRecipient = useCallback(async (recipientName: string) => {
    const id = recipientName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    try {
      await addRecipientToWhitelist({
        id,
        names: [recipientName],
        normalizedName: recipientName,
        ustId: ''
      })
      showToast(t('"{name}" added to the recipient whitelist').replace('{name}', recipientName))
    } catch (error) {
      console.error('Failed to add recipient:', error)
      showToast(error instanceof Error ? error.message : t('Could not add the recipient'), true)
    }
  }, [addRecipientToWhitelist, showToast, t])

  // Handle adding to database
  const handleAddToDatabase = useCallback(async (extraction: InvoiceExtraction) => {
    const supplierName = extraction.supplier || ''
    const rechnungsnummer = extraction.invoice_number || ''
    const dateInvoice = extraction.invoice_date || null

    // Check for duplicates first
    try {
      const dupResult = await checkDuplicate({
        supplierName,
        rechnungsnummer,
        documentType: extraction.document_type,
      })

      if (dupResult.isDuplicate) {
        // Store pending extraction and show warning dialog
        setPendingExtraction(extraction)
        setDuplicateInfo({
          supplierName,
          rechnungsnummer,
          dateInvoice,
          duplicateId: dupResult.duplicateIds[0] || null,
        })
        setDuplicateOpen(true)
        return
      }
    } catch (error) {
      console.error('Duplicate check failed:', error)
      // Continue with creation on error
    }

    // Actually create the invoice
    await actuallyCreateInvoice(extraction)
  }, [checkDuplicate, currentDocumentId])

  // Separate function for actual invoice creation
  const actuallyCreateInvoice = useCallback(async (extraction: InvoiceExtraction) => {
    try {
      await createInvoice({
        supplierName: extraction.supplier || '',
        dateInvoice: extraction.invoice_date || null,
        brutto: extraction.total_gross ?? null,
        netto: extraction.total_net ?? null,
        rechnungsnummer: extraction.invoice_number || '',
        comment: '',
        currency: extraction.currency || null,
        projectName: extraction.project_name || null,
        recipient: extraction.recipient || null,
        ustId: extraction.ust_id || null,
        confidence: extraction.confidence,
        source: 'analyser',
        vatAmount: extraction.vat_amount ?? null,
        taxFreeAmount: extraction.tax_free_amount ?? null,
        taxableNetAmount: extraction.taxable_net_amount ?? null,
        vatBreakdown: extraction.vat_breakdown ?? null,
        taxFreeBreakdown: extraction.tax_free_breakdown ?? null,
        evidence: extraction.evidence ?? null,
        warnings: extraction.warnings ?? null,
        sourceDocumentId: currentDocumentId,
        poId: extraction.po_id ?? null,
        documentType: extraction.document_type ?? 'invoice',
        customFields: {},
      })
      showToast(t('Added to the database'))
    } catch (error) {
      console.error('Failed to add to database:', error)
      showToast(error instanceof Error ? error.message : t('Could not add to the database'), true)
    }
  }, [createInvoice, currentDocumentId, showToast, t])

  /**
   * Hand the analysed document to Flixpector — the analyser's version of the
   * Inbox hand-off. The server creates the inbox item and pushes it in one call,
   * so the invoice lands under "Bei Flixpector" and syncs back from there.
   */
  const handleSendToFlixpector = useCallback(
    async (extraction: InvoiceExtraction, assigneeUserId: string) => {
      if (!currentDocumentId) {
        showToast(t('No document to hand over — please upload again'), true)
        return
      }

      setSendingToFlixpector(true)
      try {
        const result = await sendAnalyserResultToFlixpector({
          documentId: currentDocumentId,
          filename: currentFilename,
          extraction,
          assigneeUserId,
          poId: extraction.po_id ?? null,
        })

        showToast(
          result.analysisStarted
            ? t('Handed over to Flixpector — now in the Inbox under "With Flixpector".')
            : t('Handed over, but the analysis in Flixpector did not start — trigger it there.'),
        )
        // Clear the card, as the Inbox closes a handed-over item: the decision
        // is made, and pressing again would only be refused as a second push.
        handleNewUpload()
        await refreshHistory()
      } catch (error) {
        console.error('Failed to push to Flixpector:', error)
        showToast(error instanceof Error ? error.message : t('Hand-over failed'), true)
      } finally {
        setSendingToFlixpector(false)
      }
    },
    [currentDocumentId, currentFilename, handleNewUpload, refreshHistory, showToast, t],
  )

  // Handle duplicate warning confirm
  const handleDuplicateConfirm = useCallback(() => {
    setDuplicateOpen(false)
    if (pendingExtraction) {
      void actuallyCreateInvoice(pendingExtraction)
      setPendingExtraction(null)
    }
  }, [pendingExtraction, actuallyCreateInvoice])

  // Handle duplicate warning cancel
  const handleDuplicateCancel = useCallback(() => {
    setDuplicateOpen(false)
    setPendingExtraction(null)
    setDuplicateInfo(null)
  }, [])


  return (
    <Page>
      <PageHeader
        title={t('Invoice Analyser')}
        actions={
          <Button variant="outline" onClick={() => void refreshHistory()} disabled={loading}>
            <Icon name="refresh" size={15} strokeWidth={2.2} />
            {t('Refresh history')}
          </Button>
        }
      />

      {toastMessage &&
        (toastIsError ? (
          <Banner tone="err">{toastMessage}</Banner>
        ) : (
          <Notice onClose={() => setToastMessage(null)}>{toastMessage}</Notice>
        ))}

      {duplicateInfo && (
        <DuplicateWarning
          open={duplicateOpen}
          supplierName={duplicateInfo.supplierName}
          rechnungsnummer={duplicateInfo.rechnungsnummer}
          dateInvoice={duplicateInfo.dateInvoice}
          duplicateId={duplicateInfo.duplicateId}
          onConfirm={handleDuplicateConfirm}
          onCancel={handleDuplicateCancel}
        />
      )}

      {/* Dropzone and processing card share one row, as in the design */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {showDropzone && !currentResult && (
          <DocumentDropzone onFileSelect={handleFileSelect} isUploading={isUploading} />
        )}
        {currentJob && currentJob.status !== 'completed' && (
          <ProcessingStatus job={currentJob} filename={currentFilename} />
        )}
      </div>

      {currentJob?.status === 'failed' && (
        <Banner
          tone="err"
          actions={
            <Button variant="ghost" size="sm" onClick={handleNewUpload}>
              {t('Try again')}
            </Button>
          }
        >
          {currentJob.error || t('Processing failed.')}
        </Banner>
      )}

      {currentResult && (
        <ExtractionResult
          result={currentResult}
          filename={currentFilename}
          onViewEvidence={handleViewEvidence}
          onAddSupplierToWhitelist={handleAddSupplier}
          onAddRecipientToWhitelist={handleAddRecipient}
          onAddToDatabase={handleAddToDatabase}
          onSendToFlixpector={handleSendToFlixpector}
          sendingToFlixpector={sendingToFlixpector}
          onDiscard={handleNewUpload}
        />
      )}

      <HistoryPanel
        history={history}
        open={historyOpen}
        onToggle={() => setHistoryOpen((v) => !v)}
        onItemClick={handleHistoryItemClick}
        onDeleteItem={handleDeleteItem}
      />

      <EvidenceDialog
        open={evidenceOpen}
        onClose={() => setEvidenceOpen(false)}
        extraction={currentResult}
        focusField={evidenceField}
      />
    </Page>
  )
}
